import { TASK_BORDER_RADIUS, TASK_HEIGHT, TASK_SELECTED_BORDER_WIDTH, TASK_WIDTH } from '../config'
import { Vector2 } from './vector2'

// Two naming schemes using cardinal directions:
//   XS:  Side
//   XXC: Corner
//
// Labelled "Diagram":
//      ____________
// NWC /     NS     \ NEC
//     |            |
//  WS |            | ES
//     |            |
// SWC \____________/ SEC
//           SS

const TWICE_PI = Math.PI * 2

function remainder (a: number, b: number) {
  return ((a % b) + b) % b
}

interface MeasurementSet {
  readonly height: number;
  readonly width: number;
  readonly cornerRadius: number;

  readonly halfHeight: number;
  readonly halfWidth: number;

  // Center of the corner radius arc.
  readonly necArcX: number;
  readonly necArcY: number;

  // Angles from the origin that each line starts and ends in counterclockwise
  // order along the +X axis.
  readonly esEnd: number;
  readonly nsStart: number;
}

function makeMeasurementSet (width: number, height: number, cornerRadius: number): MeasurementSet {
  return {
    width,
    height,
    cornerRadius,
    halfHeight: height / 2,
    halfWidth: width / 2,
    necArcX: width / 2 - cornerRadius,
    necArcY: height / 2 - cornerRadius,
    esEnd: Math.atan2(height / 2 - cornerRadius, width / 2),
    nsStart: Math.atan2(height / 2, width / 2 - cornerRadius)
  }
}

const selectedAngleSet = makeMeasurementSet(
  TASK_WIDTH + TASK_SELECTED_BORDER_WIDTH * 2,
  TASK_HEIGHT + TASK_SELECTED_BORDER_WIDTH * 2,
  TASK_BORDER_RADIUS
)
const normalAngleSet = makeMeasurementSet(
  TASK_WIDTH,
  TASK_HEIGHT,
  TASK_BORDER_RADIUS
)

// Find y intersection of ray coming from origin with vertical axis.
const rayLine = (angle: number, x: number) => Math.tan(angle) * x

// Find x, y intersection of ray coming from origin with far edge of circle.
function _rayCircle (angle: number, circleX: number, circleY: number, radius: number): Vector2 {
  // Based on https://math.stackexchange.com/a/311956

  // x0 = 0, y0 = 0, x1 = 1
  const y1 = Math.tan(angle)

  const a = 1 + y1 * y1
  const b = -(2 * circleX + 2 * y1 * circleY)
  const c = circleX * circleX + circleY * circleY - radius * radius

  // Solve max t using quadratic equation, breaking it into common terms.
  const p = 2 * c
  const q = Math.sqrt(b * b - 4 * a * c)
  const t = Math.max(p / (q - b), -p / (b + q))

  return { x: t, y: t * y1 }
}

type VectorTransform = (v: Vector2) => Vector2;
const identity = (i: Vector2) => i
const flipX = (i: Vector2) => ({ x: -i.x, y: i.y })
const flipY = (i: Vector2) => ({ x: i.x, y: -i.y })
const flipXY = (i: Vector2) => ({ x: -i.x, y: -i.y })

export function taskEdge (angle: number, selected: boolean): Vector2 {
  const a = selected ? selectedAngleSet : normalAngleSet

  // Preprocess the angle and post-process the outgoing intersection point so
  // that our calculations only have to deal with angles between 0 and 90deg.
  let xform: VectorTransform
  angle = remainder(angle, TWICE_PI)
  if (angle < (Math.PI / 2)) {
    xform = identity
  } else if (angle < Math.PI) {
    angle = Math.PI - angle
    xform = flipX
  } else if (angle < (3 * Math.PI / 2)) {
    angle = angle - Math.PI
    xform = flipXY
  } else {
    angle = 2 * Math.PI - angle
    xform = flipY
  }

  let vec: Vector2
  if (angle < a.esEnd) {
    vec = { x: a.halfWidth, y: rayLine(angle, a.halfWidth) }
  } else if (angle < a.nsStart) {
    vec = _rayCircle(angle, a.necArcX, a.necArcY, a.cornerRadius)
  } else {
    vec = { x: rayLine(Math.PI / 2 - angle, a.halfHeight), y: a.halfHeight }
  }

  return xform(vec)
}
